import { DeleteOutlined } from '@ant-design/icons';
import { OwnerSelectDropdown } from '@repo/ui';
import { translate, TranslationEntity } from '@repo/utils';
import { FILTERABLE_ENTITY_TYPES } from 'advanced-filters/data';
import {
  Button,
  Card, Col, Divider, Flex, Input, Row, Typography,
} from 'antd';
import { type DistributionRuleType } from 'crm-atoms/services/leadDistributionService';
import { useCallback } from 'react';

import AutomationsCriteriaConditionsWrapper from '../AutomationsCriteriaConditionsWrapper';
import { type BuilderUiProps } from '../AutomationsCriteriaConditionsWrapper/AutomationsCriteriaConditionsWrapper';
import ShowConditionsUi from '../ShowConditionsUi';
import styles from './CreateLeadDistributionForm.module.scss';

export default function DistributionRuleUi({
  rule,
  onChange,
  onDelete,
  index,
}: {
  rule: Partial<DistributionRuleType>;
  onChange: (rule: Partial<DistributionRuleType>) => void;
  onDelete: () => void;
  index: number;
}) {
  const handleUpdateOwners = useCallback((owners: string | string[]) => {
    if (Array.isArray(owners)) {
      onChange({ ...rule, entities: owners.map((owner) => ({ entityUuid: owner })) });
    } else {
      onChange({ ...rule, entities: [{ entityUuid: owners }] });
    }
  }, [onChange, rule]);

  const handleUpdateCriteriaGroup = useCallback((criteriaId: number | undefined) => {
    onChange({ ...rule, criteriaGroupId: criteriaId });
  }, [onChange, rule]);

  const ConditionsBuilderUi = useCallback((props: BuilderUiProps) => (
    <ShowConditionsUi
      {...props}
      title="Set Conditions"
      btnClassName={styles.addBtn}
    />
  ), []);

  return (
    <Card
      title="Set Rule"
      extra={index > 0 && (
        <Button
          icon={<DeleteOutlined />}
          danger
          onClick={onDelete}
          type="text"
        >
          Delete Rule
        </Button>
      )}
    >
      <Flex vertical className={styles.container}>
        <Row className={styles.row}>
          <Col span={5}>
            <Typography.Text strong>Rule Name:</Typography.Text>
          </Col>
          <Col span={18}>
            <Input
              placeholder="Enter Rule Name"
              value={rule.name}
              className={styles.cardTitle}
              onChange={(e) => onChange({ ...rule, name: e.target.value })}
            />
          </Col>
        </Row>
        <Divider dashed />
        <Row className={styles.row}>
          <Col span={5}>
            <Typography.Text strong>Choose condition(s) to allow Lead(s) in this automation</Typography.Text>
          </Col>
          <Col span={18}>
            <AutomationsCriteriaConditionsWrapper
              criteriaId={rule.criteriaGroupId}
              onCriteriaChange={handleUpdateCriteriaGroup}
              builderUi={ConditionsBuilderUi}
              excludedDefaultKeys={
                Object.values(FILTERABLE_ENTITY_TYPES).filter(
                  (key) => key !== FILTERABLE_ENTITY_TYPES.leadAttribute,
                )
              }
            />
          </Col>
        </Row>
        <Divider dashed />
        <Row className={styles.row}>
          <Col span={5}>
            <Typography.Text strong>
              {translate(`If the above condition is met, the #{${TranslationEntity.lead}} will be assigned to:`)}
            </Typography.Text>
          </Col>
          <Col span={18}>
            <OwnerSelectDropdown
              value={rule.entities?.map((entity) => entity.entityUuid)}
              className={styles.ownerSelect}
              multiple
              handleClick={handleUpdateOwners}
              selectProps={{
                placeholder: 'Select Owners',
              }}
            />
          </Col>
        </Row>
      </Flex>
    </Card>
  );
}
